import * as THREE from 'three';
import { BLACK, BONE, DSTEEL, DWOOD, STEEL, WOOD, basic, emis, smoke, std, stone } from '../materials.ts';
import { G, M, Ribbon, V, eyesGlow, flame, glowSprite, limb, ring, wisp, type Flame } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/* ---------- goblin (the corruption ladder) ---------- */

/** The goblin's pivots (their `abs` frames take absolute model coordinates). */
interface GoblinRig {
  body: THREE.Group;
  head: THREE.Group;
  jaw: THREE.Group;
  arm: [THREE.Group, THREE.Group];
  fore: [THREE.Group, THREE.Group];
  leg: [THREE.Group, THREE.Group];
  shin: [THREE.Group, THREE.Group];
  skel: Record<string, THREE.Object3D>;
}

const goblinRig = (g: THREE.Object3D): GoblinRig => {
  const body = joint(g, V(0, 0.52, 0), 'body');
  const head = joint(body.abs, V(0, 0.98, 0.12), 'head');
  const jaw = joint(head.abs, V(0, 1.03, 0.22), 'jaw');
  const arm = [-1, 1].map((k) => joint(body.abs, V(k * 0.24, 0.95, 0.08), k < 0 ? 'armL' : 'armR'));
  const fore = [-1, 1].map((k, i) => joint(arm[i]!.abs, V(k * 0.36, 0.66, 0.16), k < 0 ? 'foreL' : 'foreR'));
  const leg = [-1, 1].map((k) => joint(g, V(k * 0.13, 0.52, 0), k < 0 ? 'legL' : 'legR'));
  const shin = [-1, 1].map((k, i) => joint(leg[i]!.abs, V(k * 0.19, 0.3, 0.12), k < 0 ? 'shinL' : 'shinR'));
  const two = <T>(a: T[]): [T, T] => [a[0]!, a[1]!];
  return {
    body: body.abs, head: head.abs, jaw: jaw.abs, arm: two(arm.map((j) => j.abs)), fore: two(fore.map((j) => j.abs)), leg: two(leg.map((j) => j.abs)), shin: two(shin.map((j) => j.abs)),
    skel: { body: body.p, head: head.p, jaw: jaw.p, armL: arm[0]!.p, foreL: fore[0]!.p, armR: arm[1]!.p, foreR: fore[1]!.p, legL: leg[0]!.p, shinL: shin[0]!.p, legR: leg[1]!.p, shinR: shin[1]!.p },
  };
};

const goblinBody = (r: GoblinRig, mat: THREE.Material): void => {
  const body = M(new THREE.SphereGeometry(0.3, 12, 9), mat, r.body);
  body.scale.set(1, 1.1, 0.85);
  body.position.set(0, 0.74, 0);
  body.rotation.x = 0.35;
  const head = M(new THREE.SphereGeometry(0.16, 12, 9), mat, r.head);
  head.position.set(0, 1.1, 0.2);
  head.scale.set(1, 0.9, 1.1);
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const ear = M(new THREE.ConeGeometry(0.05, 0.26, 5), mat, r.head);
    ear.position.set(k * 0.2, 1.16, 0.17);
    ear.rotation.z = -k * 1.25;
    limb(r.arm[i], V(k * 0.24, 0.95, 0.08), V(k * 0.36, 0.66, 0.16), 0.04, 0.05, mat, 6);
    limb(r.fore[i], V(k * 0.36, 0.66, 0.16), V(k * 0.32, 0.4, 0.24), 0.03, 0.04, mat, 6);
    for (let c = 0; c < 3; c++) {
      const cl = M(new THREE.ConeGeometry(0.012, 0.07, 4), BONE, r.fore[i]);
      cl.position.set(k * 0.32 + (c - 1) * 0.02, 0.35, 0.26);
      cl.rotation.x = Math.PI;
    }
    limb(r.leg[i], V(k * 0.13, 0.52, 0), V(k * 0.19, 0.3, 0.12), 0.05, 0.06, mat, 6);
    limb(r.shin[i], V(k * 0.19, 0.3, 0.12), V(k * 0.16, 0.04, 0), 0.04, 0.05, mat, 6);
    M(new THREE.BoxGeometry(0.09, 0.05, 0.16), mat, r.shin[i]).position.set(k * 0.16, 0.025, 0.04);
  }
  M(new THREE.BoxGeometry(0.18, 0.05, 0.1), mat, r.jaw).position.set(0, 1.0, 0.3);
  for (let i = 0; i < 4; i++) {
    const tooth = M(new THREE.ConeGeometry(0.012, 0.04, 4), BONE, r.jaw);
    tooth.position.set(-0.06 + i * 0.04, 1.035, 0.34);
    tooth.rotation.x = Math.PI;
  }
};

export interface GoblinOpts extends BaseOpts {
  /** Corruption grade 1 shadow, 2 ink, 3 ash, 4 obsidian, 5 abyss. */
  tier: 1 | 2 | 3 | 4 | 5;
  /** Warlord: crown of flames and axe, 1.2x size. */
  elite?: boolean;
}

export const goblin = (o: GoblinOpts): Model => {
  const c = begin(o, 0.62, 20 + o.tier + (o.elite ? 10 : 0));
  const { g, R, rng } = c;
  const rig = goblinRig(g);
  const bd = rig.body;
  const ribs: Ribbon[] = [];
  const anim: ((n: number) => void)[] = [];
  let mat: THREE.Material;
  let eyeC: string;
  const tier = o.tier;
  if (tier === 1) {
    mat = smoke('#1d1730', 0.6);
    eyeC = '#c9b8ff';
    const wm = basic('#2b2244', 0.45);
    for (let i = 0; i < 5; i++) {
      const a = i * 1.25;
      ribs.push(new Ribbon(bd, wisp(Math.cos(a) * 0.2, Math.sin(a) * 0.15, 1.3, 9).map((p) => p.add(V(0, 0.25, 0))), 0.03, wm, rng, 0.05));
    }
  } else if (tier === 2) {
    mat = std('#07060c', { roughness: 0.05, metalness: 0.5, bumpScale: 0 });
    eyeC = '#e9e4ff';
    for (let i = 0; i < 6; i++) {
      const d = M(new THREE.SphereGeometry(0.03, 6, 5), mat, bd);
      d.scale.y = 2.4;
      const ph = i / 6, x = R() * 0.5, z = R() * 0.3 + 0.1;
      anim.push((n) => {
        const p = (ph + n * 0.08) % 1;
        d.position.set(x, 0.75 - p * 0.7, z);
      });
    }
  } else if (tier === 3) {
    mat = std('#3f3a35', { roughness: 1, flatShading: true, bumpScale: 0.05 });
    eyeC = '#ff8a2a';
    const crack = emis('#ff9a3a', '#ff5a10', 1.1);
    for (let i = 0; i < 5; i++) {
      const a = i * 1.3;
      const pts: THREE.Vector3[] = [];
      for (let j = 0; j < 6; j++) pts.push(V(Math.cos(a + j * 0.2) * 0.31, 0.55 + j * 0.08, Math.sin(a + j * 0.2) * 0.26));
      ribs.push(new Ribbon(bd, pts, 0.012, crack, rng, 0.01));
    }
    for (let i = 0; i < 6; i++) {
      const e = glowSprite(bd, '#ff7a1a', 0.08, V(0, 1, 0), 0.9, true);
      const ph = i / 6, a = i * 1.1;
      anim.push((n) => {
        const p = (ph + n * 0.05) % 1;
        e.position.set(Math.cos(a + p * 3) * 0.25, 0.6 + p * 1.1, Math.sin(a + p * 3) * 0.2);
        e.material.opacity = 0.9 * (1 - p);
      });
    }
  } else if (tier === 4) {
    mat = std('#120f18', { roughness: 0.12, metalness: 0.35, flatShading: true, bumpScale: 0 });
    eyeC = '#b07bff';
    const sh = std('#0b0910', { roughness: 0.08, metalness: 0.4, flatShading: true });
    for (let i = 0; i < 9; i++) {
      const s = M(new THREE.OctahedronGeometry(0.07 + rng() * 0.06), sh, bd);
      s.scale.y = 2.2;
      const a = rng() * Math.PI * 2;
      s.position.set(Math.cos(a) * 0.22, 0.8 + rng() * 0.3, -0.12 + Math.sin(a) * 0.12);
      s.rotation.set(R(), R(), R());
    }
    const gl = emis('#c9a2ff', '#8b4dff', 1);
    for (let i = 0; i < 4; i++) {
      const pts: THREE.Vector3[] = [];
      for (let j = 0; j < 5; j++) pts.push(V(R() * 0.4, 0.5 + j * 0.12, 0.2 + R() * 0.1));
      ribs.push(new Ribbon(bd, pts, 0.008, gl, rng, 0.015));
    }
  } else {
    mat = std('#020203', { roughness: 1, bumpScale: 0 });
    eyeC = '#ff7bf2';
    for (let i = 0; i < 7; i++) {
      const s = glowSprite(bd, '#9b4dff', 0.5, V(R() * 0.4, 0.6 + rng() * 0.6, R() * 0.3), 0.35, true);
      const ph = i / 7;
      anim.push((n) => {
        const p = (ph + n * 0.06) % 1;
        s.material.opacity = 0.35 * (1 - p) + 0.05;
        s.position.y = 0.6 + p * 0.8;
      });
    }
    const halo = G(rig.head, 0, 1.45, 0);
    const rm = emis('#e0b8ff', '#a056ff', 1.2);
    ring(halo, 0.24, 0.008, rm, 0, 4, 32);
    for (let i = 0; i < 8; i++) {
      const gl = M(new THREE.BoxGeometry(0.04, 0.04, 0.004), rm, halo);
      const a = (i / 8) * Math.PI * 2;
      gl.position.set(Math.cos(a) * 0.24, 0, Math.sin(a) * 0.24);
      gl.rotation.y = -a;
    }
    anim.push((n) => { halo.rotation.y = n * 0.25; });
  }
  goblinBody(rig, mat);
  eyesGlow(rig.head, eyeC, 1.13, 0.34, 0.06);
  const S = o.elite ? 1.2 : 1;
  const flames: Flame[] = [];
  if (o.elite) {
    const crown = G(rig.head, 0, 1.27, 0.2);
    ring(crown, 0.13, 0.02, DSTEEL, 0, 5, 16);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      M(new THREE.ConeGeometry(0.02, 0.12, 5), DSTEEL, crown).position.set(Math.cos(a) * 0.13, 0.06, Math.sin(a) * 0.13);
    }
    for (let i = 0; i < 3; i++) flames.push(flame(crown, V(Math.cos(i * 2.1) * 0.13, 0.1, Math.sin(i * 2.1) * 0.13), 0.5, rng));
    const axe = G(rig.fore[1], 0.32, 0.4, 0.24);
    limb(axe, V(0, -0.1, 0), V(0, 0.6, 0), 0.018, 0.018, DWOOD, 6);
    const bl = M(new THREE.CylinderGeometry(0.14, 0.14, 0.02, 10, 1, false, 0, Math.PI), DSTEEL, axe);
    bl.rotation.z = Math.PI / 2;
    bl.position.set(0.02, 0.52, 0);
  }
  // The breathing squash lives in the clips now; the frames only boil.
  g.scale.setScalar(S);
  const t = (n: number): void => {
    for (const r of ribs) r.step();
    for (const f of flames) f.flick();
    for (const a of anim) a(n);
  };
  t(0);
  return done(c, 1.45 * S, t, undefined, bonesOf(c, rig.skel));
};

/* ---------- bestiary ---------- */

/**
 * Rigged like the goblin: each model places its parts with the same absolute coordinates as always, only
 * inside the `abs` frame of the bone it rides (see `joint()`), so at rest it looks exactly as it did. The
 * motion is in anim/creatures/nightmares.ts; `tick` only boils (ribbons, ooze, drifting sparks).
 */

export const wolf = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 31);
  const { g, R, rng } = c;
  const mat = smoke('#151122', 0.85, { depthWrite: true });
  const ribs: Ribbon[] = [];
  // Spine in two: the haunches carry the hind legs and the tail, the chest the forelegs and the head.
  const body = joint(g, V(0, 0.55, -0.05), 'body');
  const chest = joint(body.abs, V(0, 0.6, 0.2), 'chest');
  const neck = joint(chest.abs, V(0, 0.7, 0.45), 'head');
  const jaw = joint(neck.abs, V(0, 0.735, 0.645), 'jaw');
  const bodyM = M(new THREE.SphereGeometry(0.26, 12, 9), mat, body.abs);
  bodyM.scale.set(1, 0.85, 2);
  bodyM.position.y = 0.55;
  M(new THREE.SphereGeometry(0.24, 12, 9), mat, chest.abs).position.set(0, 0.6, 0.32);
  const head = G(neck.abs, 0, 0.78, 0.58);
  M(new THREE.SphereGeometry(0.15, 10, 8), mat, head);
  const snout = M(new THREE.ConeGeometry(0.08, 0.24, 7), mat, head);
  snout.rotation.x = Math.PI / 2;
  snout.position.set(0, -0.03, 0.18);
  // The lower jaw: tucked inside the muzzle at rest, it drops open to bite.
  const jawG = G(jaw.abs, 0, 0.78, 0.58);
  const lower = M(new THREE.ConeGeometry(0.05, 0.18, 6), mat, jawG);
  lower.rotation.x = Math.PI / 2;
  lower.position.set(0, -0.045, 0.155);
  for (const k of [-1, 1]) M(new THREE.ConeGeometry(0.05, 0.14, 5), mat, head).position.set(k * 0.08, 0.15, -0.02);
  eyesGlow(head, '#9fd0ff', 0.03, 0.12, 0.06);
  const skel: Record<string, THREE.Object3D> = { body: body.p, chest: chest.p, head: neck.p, jaw: jaw.p };
  for (const [x, z] of [[0.13, 0.35], [-0.13, 0.35], [0.13, -0.3], [-0.13, -0.3]] as const) {
    const front = z > 0;
    const side = x < 0 ? 'L' : 'R';
    const name = front ? 'fore' : 'hind';
    const leg = joint(front ? chest.abs : body.abs, V(x, 0.5, z), `${name}${side}`);
    const low = joint(leg.abs, V(x, 0.25, z + 0.05), `${name}${side}2`);
    limb(leg.abs, V(x, 0.5, z), V(x, 0.25, z + 0.05), 0.05, 0.06, mat, 6);
    limb(low.abs, V(x, 0.25, z + 0.05), V(x, 0.02, z), 0.035, 0.045, mat, 6);
    skel[`${name}${side}`] = leg.p;
    skel[`${name}${side}2`] = low.p;
  }
  // The tail in three links, so it can stream and lash.
  const tail: THREE.Vector3[] = [];
  for (let i = 0; i < 7; i++) tail.push(V(0, 0.6 + i * 0.03, -0.45 - i * 0.07));
  let parent = body.abs;
  for (let s = 0; s < 3; s++) {
    const tj = joint(parent, tail[s * 2]!, `tail${s + 1}`);
    ribs.push(new Ribbon(tj.abs, tail.slice(s * 2, s * 2 + 3), 0.04 - s * 0.004, mat, rng, 0.03));
    skel[`tail${s + 1}`] = tj.p;
    parent = tj.abs;
  }
  const wm = basic('#2b2244', 0.4);
  for (let i = 0; i < 5; i++) {
    const pts: THREE.Vector3[] = [];
    const x = R() * 0.3, z = R() * 0.6;
    for (let j = 0; j < 6; j++) pts.push(V(x + R() * 0.05, 0.7 + j * 0.08, z - j * 0.05));
    ribs.push(new Ribbon(z > 0.1 ? chest.abs : body.abs, pts, 0.02, wm, rng, 0.04));
  }
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  return done(c, 0.95, t, undefined, bonesOf(c, skel));
};

export const skeleton = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 32);
  const { g } = c;
  const bm = BONE;
  const hips = joint(g, V(0, 0.92, 0), 'hips');
  const body = joint(hips.abs, V(0, 0.98, -0.02), 'body');
  const head = joint(body.abs, V(0, 1.47, -0.02), 'head');
  const jawJ = joint(head.abs, V(0, 1.55, -0.02), 'jaw');
  const skull = M(new THREE.SphereGeometry(0.11, 10, 8), bm, head.abs);
  skull.position.y = 1.62;
  skull.scale.set(0.9, 1, 1);
  const jaw = M(new THREE.BoxGeometry(0.12, 0.04, 0.09), bm, jawJ.abs);
  jaw.position.set(0, 1.52, 0.03);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.03, 6, 5), BLACK, head.abs).position.set(k * 0.04, 1.63, 0.085);
  eyesGlow(head.abs, '#ff6a2a', 1.63, 0.1, 0.04);
  for (let i = 0; i < 8; i++) M(new THREE.CylinderGeometry(0.025, 0.025, 0.05, 6), bm, body.abs).position.set(0, 1.0 + i * 0.06, -0.03);
  for (let i = 0; i < 4; i++) {
    const rib = M(new THREE.TorusGeometry(0.13 - i * 0.01, 0.012, 4, 10, Math.PI * 1.4), bm, body.abs);
    rib.rotation.set(Math.PI / 2, 0, Math.PI * 0.8);
    rib.position.set(0, 1.38 - i * 0.07, 0);
  }
  ring(hips.abs, 0.1, 0.025, bm, 0.92, 5, 10);
  const skel: Record<string, THREE.Object3D> = { hips: hips.p, body: body.p, head: head.p, jaw: jawJ.p };
  const hands: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const leg = joint(hips.abs, V(k * 0.08, 0.9, 0), `leg${s}`);
    const shin = joint(leg.abs, V(k * 0.1, 0.48, 0.04), `shin${s}`);
    const arm = joint(body.abs, V(k * 0.18, 1.4, 0), `arm${s}`);
    const fore = joint(arm.abs, V(k * 0.22, 1.14, 0.06), `fore${s}`);
    limb(leg.abs, V(k * 0.08, 0.9, 0), V(k * 0.1, 0.48, 0.04), 0.02, 0.022, bm, 6);
    limb(shin.abs, V(k * 0.1, 0.48, 0.04), V(k * 0.09, 0.05, 0), 0.018, 0.02, bm, 6);
    M(new THREE.BoxGeometry(0.06, 0.03, 0.12), bm, shin.abs).position.set(k * 0.09, 0.02, 0.03);
    limb(arm.abs, V(k * 0.18, 1.4, 0), V(k * 0.22, 1.14, 0.06), 0.016, 0.018, bm, 6);
    limb(fore.abs, V(k * 0.22, 1.14, 0.06), V(k * 0.25, 0.93, 0.14), 0.014, 0.016, bm, 6);
    Object.assign(skel, { [`arm${s}`]: arm.p, [`fore${s}`]: fore.p, [`leg${s}`]: leg.p, [`shin${s}`]: shin.p });
    hands.push(fore.abs);
  }
  const sh = M(new THREE.CylinderGeometry(0.2, 0.2, 0.04, 14), WOOD, hands[0]!);
  sh.rotation.set(Math.PI / 2, 0, 0);
  sh.position.set(-0.3, 1.0, 0.18);
  M(new THREE.TorusGeometry(0.2, 0.015, 5, 16), DSTEEL, hands[0]!).position.set(-0.3, 1.0, 0.2);
  // The blade turns at the wrist.
  const grip = joint(hands[1]!, V(0.25, 0.93, 0.14), 'blade');
  skel.blade = grip.p;
  const piv = G(grip.abs, 0.25, 0.93, 0.14);
  piv.rotation.x = 0.2;
  const bl = new THREE.Shape();
  bl.moveTo(0, 0);
  bl.quadraticCurveTo(0.12, 0.3, 0.02, 0.62);
  bl.lineTo(-0.02, 0.6);
  bl.quadraticCurveTo(0.06, 0.3, -0.03, 0);
  bl.closePath();
  M(new THREE.ExtrudeGeometry(bl, { depth: 0.01, bevelEnabled: false, curveSegments: 6 }), STEEL, piv).position.y = 0.05;
  return done(c, 1.75, undefined, undefined, bonesOf(c, skel));
};

export const wraith = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 33);
  const { g, rng } = c;
  // It floats: the hover height is its rest pose, the clips bob it.
  g.position.y = 0.25;
  const mat = smoke('#2a2f3a', 0.65, { side: THREE.DoubleSide });
  // The robe hangs from the shoulders like a bell; the hood and the arms ride on it.
  const body = joint(g, V(0, 1.45, 0), 'body');
  const head = joint(body.abs, V(0, 1.48, 0.02), 'head');
  const geo = new THREE.ConeGeometry(0.42, 1.3, 14, 5, true);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) if (pos.getY(i) < -0.6) pos.setY(i, pos.getY(i) + rng() * 0.25);
  geo.computeVertexNormals();
  M(geo, mat, body.abs).position.y = 0.95;
  const hood = M(new THREE.SphereGeometry(0.17, 12, 9, 0, Math.PI * 2, 0, Math.PI / 1.5), mat, head.abs);
  hood.position.set(0, 1.62, 0.02);
  hood.rotation.x = -0.3;
  M(new THREE.SphereGeometry(0.12, 8, 6), BLACK, head.abs).position.set(0, 1.58, 0.05);
  eyesGlow(head.abs, '#a8f0ff', 1.6, 0.16, 0.045);
  const skel: Record<string, THREE.Object3D> = { body: body.p, head: head.p };
  for (const k of [-1, 1]) {
    const arm = joint(body.abs, V(k * 0.18, 1.35, 0.05), k < 0 ? 'armL' : 'armR');
    skel[k < 0 ? 'armL' : 'armR'] = arm.p;
    limb(arm.abs, V(k * 0.18, 1.35, 0.05), V(k * 0.42, 1.2, 0.3), 0.04, 0.03, mat, 6);
    for (let cl = 0; cl < 3; cl++) {
      const m = M(new THREE.ConeGeometry(0.01, 0.12, 4), BONE, arm.abs);
      m.position.set(k * 0.44 + (cl - 1) * 0.02, 1.18, 0.36);
      m.rotation.x = Math.PI / 2;
    }
  }
  // The trailing wisps in two sets that sway out of step.
  const ribs: Ribbon[] = [];
  const wm = basic('#3b4456', 0.4);
  const trail = [joint(body.abs, V(0, 0.62, 0), 'wispA'), joint(body.abs, V(0, 0.62, 0), 'wispB')];
  skel.wispA = trail[0]!.p;
  skel.wispB = trail[1]!.p;
  for (let i = 0; i < 4; i++) {
    const a = i * 1.6;
    ribs.push(new Ribbon(trail[i % 2]!.abs, wisp(Math.cos(a) * 0.25, Math.sin(a) * 0.25, 0.6, 6).map((p) => p.add(V(0, 0.05, 0))), 0.025, wm, rng, 0.05));
  }
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  t();
  return done(c, 2.0, t, undefined, bonesOf(c, skel));
};

export const gargoyle = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 34);
  const { g } = c;
  const st = stone('#6f6c68');
  // The perch it was carved on: it stays at rest (the old look) and is left behind once it takes wing.
  const perch = joint(g, V(0, 0.175, 0), 'perch');
  M(new THREE.BoxGeometry(0.5, 0.35, 0.5), stone('#57544f'), perch.abs).position.y = 0.175;
  const body = joint(g, V(0, 0.5, -0.02), 'body');
  const head = joint(body.abs, V(0, 0.88, 0.1), 'head');
  const bodyM = M(new THREE.SphereGeometry(0.22, 10, 8), st, body.abs);
  bodyM.scale.set(1, 1.2, 0.9);
  bodyM.position.set(0, 0.68, 0);
  bodyM.rotation.x = 0.5;
  M(new THREE.BoxGeometry(0.2, 0.18, 0.22), st, head.abs).position.set(0, 0.95, 0.16);
  const skel: Record<string, THREE.Object3D> = { perch: perch.p, body: body.p, head: head.p };
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const horn = M(new THREE.ConeGeometry(0.03, 0.18, 5), st, head.abs);
    horn.position.set(k * 0.08, 1.08, 0.1);
    horn.rotation.set(-0.6, 0, -k * 0.4);
    const arm = joint(body.abs, V(k * 0.15, 0.75, 0.1), `arm${s}`);
    const leg = joint(body.abs, V(k * 0.12, 0.5, -0.05), `leg${s}`);
    const wj = joint(body.abs, V(k * 0.12, 0.8, -0.12), `wing${s}`);
    limb(arm.abs, V(k * 0.15, 0.75, 0.1), V(k * 0.18, 0.42, 0.2), 0.04, 0.05, st, 6);
    limb(leg.abs, V(k * 0.12, 0.5, -0.05), V(k * 0.16, 0.37, 0.12), 0.06, 0.07, st, 6);
    const w = new THREE.Shape();
    w.moveTo(0, 0); w.lineTo(0.55, 0.35); w.lineTo(0.5, 0.1); w.lineTo(0.42, 0.15); w.lineTo(0.38, -0.05); w.lineTo(0.28, 0); w.lineTo(0.2, -0.12);
    w.closePath();
    const wing = M(new THREE.ExtrudeGeometry(w, { depth: 0.02, bevelEnabled: false }), st, wj.abs);
    wing.position.set(k * 0.12, 0.8, -0.12);
    wing.scale.x = k;
    wing.rotation.y = k * 0.5;
    Object.assign(skel, { [`arm${s}`]: arm.p, [`leg${s}`]: leg.p, [`wing${s}`]: wj.p });
  }
  eyesGlow(head.abs, '#ffb43a', 0.97, 0.28, 0.05);
  return done(c, 1.15, undefined, undefined, bonesOf(c, skel));
};

export const slime = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 35);
  const { g, R } = c;
  // The ooze rocks on its base; what it has swallowed hangs inside and sloshes a beat behind.
  const body = joint(g, V(0, 0.02, 0), 'body');
  const geo = new THREE.IcosahedronGeometry(0.5, 2);
  const b0 = (geo.attributes.position as THREE.BufferAttribute).array.slice();
  const blob = M(geo, std('#0f1d24', { roughness: 0.05, metalness: 0.2, transparent: true, opacity: 0.72, bumpScale: 0 }), body.abs);
  blob.scale.y = 0.75;
  blob.position.y = 0.38;
  const core = joint(body.abs, V(0.05, 0.4, 0), 'core');
  const sk = G(core.abs, 0.05, 0.4, 0);
  M(new THREE.SphereGeometry(0.09, 8, 6), BONE, sk);
  M(new THREE.BoxGeometry(0.09, 0.03, 0.07), BONE, sk).position.set(0, -0.08, 0.02);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.022, 5, 4), BLACK, sk).position.set(k * 0.035, 0.01, 0.075);
  const skel: Record<string, THREE.Object3D> = { body: body.p, core: core.p };
  for (let i = 0; i < 3; i++) {
    const p = V(R() * 0.5, 0.3 + R() * 0.2, R() * 0.4);
    const bj = joint(body.abs, p, `bone${i}`);
    skel[`bone${i}`] = bj.p;
    const bn = M(new THREE.CylinderGeometry(0.015, 0.015, 0.22, 5), BONE, bj.abs);
    bn.position.copy(p);
    bn.rotation.set(R() * 3, R() * 3, R() * 3);
  }
  const swj = joint(body.abs, V(-0.18, 0.4, 0.05), 'blade');
  skel.blade = swj.p;
  const sw = M(new THREE.BoxGeometry(0.03, 0.36, 0.01), DSTEEL, swj.abs);
  sw.position.set(-0.18, 0.4, 0.05);
  sw.rotation.z = 0.8;
  const t = (n: number): void => {
    const p = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const x = b0[i * 3]!, y = b0[i * 3 + 1]!, z = b0[i * 3 + 2]!;
      const k = 1 + Math.sin(x * 5 + n * 0.8) * 0.05 + Math.sin(z * 6 + n * 0.6) * 0.05;
      p.setXYZ(i, x * k, y * k, z * k);
    }
    p.needsUpdate = true;
    geo.computeVertexNormals();
    sk.rotation.y = n * 0.15;
  };
  return done(c, 0.78, t, undefined, bonesOf(c, skel));
};

/** NEW: hunched long-clawed shadow hunter. Smoky, mostly transparent, with a faint shimmer and two pale eyes. */
export const stalker = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 36);
  const { g, R, rng } = c;
  const mat = smoke('#120f1c', 0.42);
  const deep = smoke('#07060c', 0.55);
  const hips = joint(g, V(0, 0.62, -0.15), 'hips');
  const chest = joint(hips.abs, V(0, 0.7, -0.05), 'chest');
  const neck = joint(chest.abs, V(0, 0.84, 0.33), 'head');
  const jawJ = joint(neck.abs, V(0, 0.8, 0.44), 'jaw');
  const torso = M(new THREE.SphereGeometry(0.24, 10, 8), mat, chest.abs);
  torso.scale.set(1, 0.85, 1.55);
  torso.position.set(0, 0.78, 0.05);
  torso.rotation.x = -0.55;
  M(new THREE.SphereGeometry(0.17, 10, 8), mat, hips.abs).position.set(0, 0.62, -0.18);
  const head = G(neck.abs, 0, 0.84, 0.42);
  const skull = M(new THREE.SphereGeometry(0.11, 10, 8), deep, head);
  skull.scale.set(0.9, 0.8, 1.4);
  const jawG = G(jawJ.abs, 0, 0.84, 0.42);
  const jaw = M(new THREE.ConeGeometry(0.07, 0.2, 6), deep, jawG);
  jaw.rotation.x = Math.PI / 2;
  jaw.position.set(0, -0.05, 0.12);
  eyesGlow(head, '#e6f2ff', 0.03, 0.12, 0.045, 0.018);
  // Spine ridge.
  for (let i = 0; i < 6; i++) {
    const sp = M(new THREE.ConeGeometry(0.02, 0.09, 4), deep, chest.abs);
    sp.position.set(0, 0.96 - i * 0.05, 0.25 - i * 0.1);
    sp.rotation.x = -0.6;
  }
  const skel: Record<string, THREE.Object3D> = { hips: hips.p, chest: chest.p, head: neck.p, jaw: jawJ.p };
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    // Long arms down to the ground, claws dragging.
    const sh = V(k * 0.2, 0.86, 0.25), el = V(k * 0.36, 0.5, 0.42), wr = V(k * 0.3, 0.14, 0.56);
    const arm = joint(chest.abs, sh, `arm${s}`);
    const fore = joint(arm.abs, el, `fore${s}`);
    limb(arm.abs, sh, el, 0.045, 0.035, mat, 6);
    limb(fore.abs, el, wr, 0.035, 0.025, mat, 6);
    for (let f = 0; f < 3; f++) {
      const cl = M(new THREE.ConeGeometry(0.012, 0.22, 4), BONE, fore.abs);
      cl.position.set(wr.x + (f - 1) * 0.035, 0.07, wr.z + 0.1);
      cl.rotation.x = Math.PI / 2 + 0.5;
      cl.rotation.z = (f - 1) * 0.25;
    }
    // Digitigrade legs.
    const hip = V(k * 0.13, 0.6, -0.2), knee = V(k * 0.2, 0.38, 0.0), ank = V(k * 0.17, 0.14, -0.18);
    const leg = joint(hips.abs, hip, `leg${s}`);
    const shin = joint(leg.abs, knee, `shin${s}`);
    const foot = joint(shin.abs, ank, `foot${s}`);
    limb(leg.abs, hip, knee, 0.06, 0.045, mat, 6);
    limb(shin.abs, knee, ank, 0.04, 0.03, mat, 6);
    limb(foot.abs, ank, V(k * 0.17, 0.02, -0.05), 0.03, 0.025, mat, 6);
    Object.assign(skel, { [`arm${s}`]: arm.p, [`fore${s}`]: fore.p, [`leg${s}`]: leg.p, [`shin${s}`]: shin.p, [`foot${s}`]: foot.p });
  }
  const ribs: Ribbon[] = [];
  const wm = basic('#231c38', 0.4);
  for (let i = 0; i < 5; i++) {
    const x = R() * 0.3, z = R() * 0.6;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 6; j++) pts.push(V(x + R() * 0.05, 0.75 + j * 0.08, z - j * 0.06));
    ribs.push(new Ribbon(chest.abs, pts, 0.025, wm, rng, 0.05));
  }
  // Shimmer: faint pale threads that boil every tick.
  const shimmer = basic('#a9a4d8', 0.16);
  for (let i = 0; i < 4; i++) {
    const a = i * 1.57;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 6; j++) pts.push(V(Math.cos(a + j * 0.5) * 0.22, 0.5 + j * 0.09, 0.05 + Math.sin(a + j * 0.5) * 0.3));
    ribs.push(new Ribbon(chest.abs, pts, 0.006, shimmer, rng, 0.04));
  }
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  return done(c, 1.0, t, undefined, bonesOf(c, skel));
};

/** NEW: a jagged wall of ribs and bones, about one hex wide (along X). */
export const boneWall = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 37);
  const { g, R, rng } = c;
  const ash = stone('#2a2624');
  const mound = M(new THREE.CylinderGeometry(0.5, 0.56, 0.14, 8, 1), ash, g);
  mound.scale.z = 0.45;
  mound.position.y = 0.07;
  // The ribs in three clumps rooted in the mound; the long bones bind them; the skulls lie loose.
  const body = joint(g, V(0, 0.1, 0), 'body');
  const clumps = [-0.33, 0, 0.33].map((x, i) => joint(body.abs, V(x, 0.1, 0), ['ribsL', 'ribsC', 'ribsR'][i]!));
  const bind = joint(body.abs, V(0, 0.35, 0), 'bind');
  const skel: Record<string, THREE.Object3D> = { body: body.p, ribsL: clumps[0]!.p, ribsC: clumps[1]!.p, ribsR: clumps[2]!.p, bind: bind.p };
  const n = 9;
  for (let i = 0; i < n; i++) {
    const x = -0.44 + (i / (n - 1)) * 0.88;
    const h = 0.55 + rng() * 0.4;
    const lean = R() * 0.25, side = i % 2 ? 1 : -1;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j <= 5; j++) {
      const t = j / 5;
      pts.push(V(x + lean * t, 0.1 + t * h, side * Math.sin(t * Math.PI) * 0.12 + side * 0.03));
    }
    const into = clumps[Math.floor(i / 3)]!.abs;
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 8, 0.028 - (i % 3) * 0.004, 5), BONE, into);
    const tip = M(new THREE.ConeGeometry(0.025, 0.1, 4), BONE, into);
    tip.position.copy(pts[5]!).add(V(0, 0.04, 0));
  }
  // Long bones crossing the ribs.
  for (let i = 0; i < 3; i++) {
    const y = 0.25 + i * 0.18 + R() * 0.05;
    limb(bind.abs, V(-0.45, y + R() * 0.1, 0.02), V(0.45, y + R() * 0.1, -0.02), 0.018, 0.018, BONE, 5);
  }
  ([[-0.25, 0.14], [0.2, -0.13], [0.36, 0.12]] as const).forEach(([x, z], i) => {
    const sj = joint(g, V(x, 0.18, z), `skull${i}`);
    skel[`skull${i}`] = sj.p;
    const sk = G(sj.abs, x, 0.18, z);
    M(new THREE.SphereGeometry(0.07, 8, 6), BONE, sk);
    for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.018, 5, 4), BLACK, sk).position.set(k * 0.025, 0.0, z > 0 ? 0.06 : -0.06);
  });
  const ribs: Ribbon[] = [];
  const glow = emis('#c9a2ff', '#8b4dff', 0.9);
  for (let i = 0; i < 3; i++) {
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 5; j++) pts.push(V(-0.4 + j * 0.2, 0.18 + R() * 0.25 + i * 0.2, R() * 0.06));
    ribs.push(new Ribbon(bind.abs, pts, 0.006, glow, rng, 0.02));
  }
  glowSprite(bind.abs, '#8b4dff', 0.9, V(0, 0.35, 0), 0.25);
  const t = (): void => {
    for (const r of ribs) r.step();
  };
  return done(c, 1.0, t, undefined, bonesOf(c, skel));
};

/** NEW (internal helper for the gallery's pit): not exported in the registry. */
export const shadowVictim = (parent: THREE.Object3D): THREE.Group => {
  const vic = G(parent, -0.1, 0.25, 0.05);
  M(new THREE.SphereGeometry(0.12, 10, 8), smoke('#1d1730', 0.6), vic);
  eyesGlow(vic, '#c9b8ff', 0.03, 0.1, 0.035);
  return vic;
};
